import { SearchType } from '@brightdrop/bd-ui';
import type { DrawingMode, ShapeType } from '@brightdrop/map-ui';
import type { CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import type {
  GeofenceListItem,
  GeofenceShapeData,
} from '~/common/models/geofences.model';
import {
  GeofenceSessionViewType,
  INITIAL_GEOFENCE_DRAWING_STATE,
  INITIAL_GEOFENCE_LIST_SESSION_STATE,
  type INITIAL_GEOFENCE_STATE,
} from './geofenceSlice.constants';

// Geofence drawing reducers
export const reducerSetGeofenceShapeType: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<ShapeType | null>
> = (state, action) => {
  state.drawing.selectedShapeType = action.payload;
  state.drawing.isDrawingEnabled = action.payload !== null;
};

export const reducerSetGeofenceDrawingMode: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<DrawingMode>
> = (state, action) => {
  state.drawing.drawingMode = action.payload;
};

export const reducerSetCurrentGeofenceShapeData: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<GeofenceShapeData | null>
> = (state, action) => {
  state.drawing.currentShapeData = action.payload;
};

export const reducerClearGeofenceDrawing: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<void>
> = (state) => {
  Object.assign(state.drawing, INITIAL_GEOFENCE_DRAWING_STATE);
};

export const reducerSetShapeCenter: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<GeoJSON.Position | null>
> = (state, action) => {
  state.drawing.targetShapeCenter = action.payload;
};

export const reducerSetShapeRadius: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<number>
> = (state, action) => {
  // Update radiusInKm in currentShapeData if it exists
  if (state.drawing.currentShapeData) {
    state.drawing.currentShapeData.radiusInKm = action.payload;
  }
};

export const reducerSetSelectedGeofence: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<GeofenceListItem | null>
> = (state, action) => {
  state.selectedGeofence = action.payload;

  // Also update the geofence in all session-scoped accumulated lists to maintain consistency
  if (action.payload) {
    const updatedGeofence = action.payload;
    const listSessions = state.sessionConfigs[GeofenceSessionViewType.LIST];

    if (listSessions) {
      // Update the geofence in all sessions where it exists
      Object.keys(listSessions).forEach((sessionId) => {
        const session = listSessions[sessionId];
        if (session) {
          const index = session.accumulatedGeofences.findIndex(
            (g) => g.id === updatedGeofence.id
          );
          if (index !== -1) {
            session.accumulatedGeofences[index] = updatedGeofence;
          }
        }
      });
    }
  }
};

export const reducerSetHoveredGeofence: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<GeofenceListItem | null>
> = (state, action) => {
  state.hoveredGeofence = action.payload;
};

// Geofence list reducers (session-scoped)
export const reducerSetGeofenceListParams: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<{ sessionId: string; paramsHash: string }>
> = (state, action) => {
  const { sessionId, paramsHash } = action.payload;

  if (
    sessionId === '__proto__' ||
    sessionId === 'constructor' ||
    sessionId === 'prototype'
  ) {
    return;
  }

  // Initialize session config if it doesn't exist
  const listConfigs = state.sessionConfigs[GeofenceSessionViewType.LIST] ?? {};
  state.sessionConfigs[GeofenceSessionViewType.LIST] = listConfigs;

  // Get or initialize session state
  const sessionState = listConfigs[sessionId] || {
    ...INITIAL_GEOFENCE_LIST_SESSION_STATE,
  };

  // If params changed, reset the list
  if (sessionState.requestParamsHash !== paramsHash) {
    listConfigs[sessionId] = {
      accumulatedGeofences: [],
      totalCount: null,
      requestParamsHash: paramsHash,
      searchCriteria: sessionState.searchCriteria,
    };
  } else {
    // Ensure session state exists even if params haven't changed
    listConfigs[sessionId] = sessionState;
  }
};

export const reducerSetGeofenceListData: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<{
    sessionId: string;
    geofences: GeofenceListItem[];
    totalCount: number | null;
    isFirstPage: boolean;
  }>
> = (state, action) => {
  const { sessionId, geofences, totalCount, isFirstPage } = action.payload;

  if (
    sessionId === '__proto__' ||
    sessionId === 'constructor' ||
    sessionId === 'prototype'
  ) {
    return;
  }

  // Initialize session config if it doesn't exist
  const listConfigs = state.sessionConfigs[GeofenceSessionViewType.LIST] ?? {};
  state.sessionConfigs[GeofenceSessionViewType.LIST] = listConfigs;

  // Get or initialize session state
  const sessionState = listConfigs[sessionId] || {
    ...INITIAL_GEOFENCE_LIST_SESSION_STATE,
  };

  if (isFirstPage) {
    // First page - replace all
    listConfigs[sessionId] = {
      ...sessionState,
      accumulatedGeofences: geofences,
      totalCount,
    };
  } else {
    // Subsequent page - accumulate
    listConfigs[sessionId] = {
      ...sessionState,
      accumulatedGeofences: [
        ...sessionState.accumulatedGeofences,
        ...geofences,
      ],
      totalCount,
    };
  }
};

export const reducerResetGeofenceList: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<{ sessionId: string }>
> = (state, action) => {
  const { sessionId } = action.payload;

  if (
    sessionId === '__proto__' ||
    sessionId === 'constructor' ||
    sessionId === 'prototype'
  ) {
    return;
  }

  // Initialize session config if it doesn't exist
  const listConfigs = state.sessionConfigs[GeofenceSessionViewType.LIST] ?? {};
  state.sessionConfigs[GeofenceSessionViewType.LIST] = listConfigs;

  // Reset the session state
  listConfigs[sessionId] = {
    ...INITIAL_GEOFENCE_LIST_SESSION_STATE,
  };
};

export const reducerUpdateGeofenceSearchCriteria: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<{ sessionId: string; input: string }>
> = (state, action) => {
  const { sessionId, input } = action.payload;

  if (
    sessionId === '__proto__' ||
    sessionId === 'constructor' ||
    sessionId === 'prototype'
  ) {
    return;
  }

  if (!sessionId) {
    return;
  }

  // Initialize session config if it doesn't exist
  if (!state.sessionConfigs[GeofenceSessionViewType.LIST]) {
    state.sessionConfigs[GeofenceSessionViewType.LIST] = {};
  }

  const geofenceListSession =
    state.sessionConfigs[GeofenceSessionViewType.LIST];

  // Get or initialize session state
  const sessionState = geofenceListSession[sessionId] || {
    ...INITIAL_GEOFENCE_LIST_SESSION_STATE,
  };

  // Update search criteria while preserving other session state
  geofenceListSession[sessionId] = {
    ...sessionState,
    searchCriteria: {
      searchType: SearchType.GEOFENCES_SEARCH,
      input,
    },
  };
};

// Rule dialog reducers - centralized state for edit/delete dialogs
export const reducerSetEditableRuleId: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<string | null>
> = (state, action) => {
  state.editableRuleId = action.payload;
};

export const reducerSetDeletingRuleId: CaseReducer<
  typeof INITIAL_GEOFENCE_STATE,
  PayloadAction<string | null>
> = (state, action) => {
  state.deletingRuleId = action.payload;
};

export const GEOFENCE_REDUCERS = {
  reducerSetGeofenceShapeType,
  reducerSetGeofenceDrawingMode,
  reducerSetCurrentGeofenceShapeData,
  reducerClearGeofenceDrawing,
  reducerSetShapeCenter,
  reducerSetShapeRadius,
  reducerSetSelectedGeofence,
  reducerSetHoveredGeofence,
  reducerSetGeofenceListParams,
  reducerSetGeofenceListData,
  reducerResetGeofenceList,
  reducerUpdateGeofenceSearchCriteria,
  reducerSetEditableRuleId,
  reducerSetDeletingRuleId,
};
